import { ChangeDetectionStrategy, Component, LOCALE_ID, computed, inject, input } from '@angular/core';
import { NgComponentOutlet, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TranslationService } from '@/layout/service/translation.service';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { MetricTileComponent } from '@/shared/ui/metric-tile/metric-tile.component';
import { LocationDto } from '@/cloud/models/tree.models';
import { RoomUsageState, describeAvailability } from '@/cloud/models/room-usage.models';
import { INSIGHT_PANELS, INSIGHT_TABS, InsightContext, InsightLinkedDevice, InsightPanelDef, InsightRange, InsightSummaryStore, InsightSummaryTile, InsightTab, insightRangeLabel } from '../insight-panel';

const RANGE_HOURS = [24, 24 * 7, 24 * 30];

/**
 * One room statistics tab (Room Usage or Power), showing the registered panels for that tab.
 * Layout, top to bottom:
 * - a toolbar: the shared time range (`?range=`) on the right, and on tabs with a summary strip a
 *   subtle "Room at a glance" heading;
 * - on tabs with a summary strip, KPI tiles: the room's live occupancy and availability (from the
 *   location's usage) followed by tiles the panels publish through `InsightSummaryStore`;
 * - one `app-section-card` per registered insight panel with data for the room, in a responsive grid
 *   (one column, two on a wide workspace; a panel with `span: 'full'` takes the whole row).
 */
@Component({
    selector: 'app-location-insights',
    standalone: true,
    imports: [NgComponentOutlet, FormsModule, SelectButtonModule, EmptyStateComponent, SectionCardComponent, MetricTileComponent],
    providers: [InsightSummaryStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (panels().length === 0) {
            <app-empty-state [icon]="tabDef().icon" [spacious]="true" [title]="t()(tabDef().labelKey + '.empty')" [description]="t()(tabDef().labelKey + '.emptyDetail')" />
        } @else {
            <div class="insights-toolbar">
                @if (summaryTiles().length) {
                    <h2 class="insights-toolbar__title" id="insights-summary-title">{{ t()('insights.summary') }}</h2>
                }
                <p-selectbutton [options]="rangeOptions()" [ngModel]="range().hours" (ngModelChange)="setRange($event)" optionLabel="label" optionValue="value" [allowEmpty]="false" size="small" [ariaLabel]="t()('metrics.range')" />
            </div>
            @if (summaryTiles().length) {
                <div class="insights-summary" role="list" aria-labelledby="insights-summary-title">
                    @for (tile of summaryTiles(); track tile.key) {
                        <app-metric-tile
                            role="listitem"
                            [class]="'insights-summary__tile insights-summary__tile--' + tile.key"
                            [label]="tile.label"
                            [value]="tile.value ?? null"
                            [text]="tile.text ?? null"
                            [unit]="tile.unit ?? ''"
                            [icon]="tile.icon ?? null"
                            [caption]="tile.caption ?? null"
                            [format]="tile.format ?? '1.0-1'"
                        />
                    }
                </div>
            }
            <div class="insights-grid">
                @for (panel of panels(); track panel.id) {
                    <app-section-card class="insight-panel" [class.insight-panel--half]="panel.span === 'half'" [attr.data-panel]="panel.id" [icon]="panel.icon" [title]="t()(panel.labelKey)" [subtitle]="rangeSubtitle()">
                        <ng-container *ngComponentOutlet="panel.component; inputs: { context: context(), range: range() }" />
                    </app-section-card>
                }
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-4);
                container: insights / inline-size;
            }
            .insights-toolbar {
                display: flex;
                flex-wrap: wrap;
                gap: var(--app-space-3);
                align-items: center;
                justify-content: space-between;
            }
            .insights-toolbar p-selectbutton {
                margin-inline-start: auto;
            }
            .insights-toolbar__title {
                margin: 0;
                font-size: 0.8125rem;
                font-weight: 600;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                color: var(--text-color-secondary);
            }
            .insights-summary {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
                gap: var(--app-space-3);
            }
            .insights-grid {
                display: grid;
                grid-template-columns: minmax(0, 1fr);
                gap: var(--app-space-4);
                align-items: start;
            }
            @container insights (min-width: 64rem) {
                .insights-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
                .insight-panel:not(.insight-panel--half) {
                    grid-column: 1 / -1;
                }
            }
        `
    ]
})
export class LocationInsightsComponent {
    private allPanels = inject(INSIGHT_PANELS, { optional: true }) ?? [];
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private locale = inject(LOCALE_ID);
    private summary = inject(InsightSummaryStore);
    private queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
    readonly t = inject(TranslationService).t;

    readonly locationId = input.required<string>();
    readonly namespaceId = input.required<string>();
    readonly linkedDevices = input<InsightLinkedDevice[]>([]);
    /** The room from the live locations list; its `usage` feeds the summary and the occupancy panel. */
    readonly location = input<LocationDto | null>(null);
    /** The room's control system is offline: usage is shown as last reported, not live. */
    readonly usageStale = input(false);
    /** Which room tab this is; only that tab's panels are shown. */
    readonly tab = input<InsightTab>('usage');

    readonly tabDef = computed(() => INSIGHT_TABS.find((t) => t.id === this.tab()) ?? INSIGHT_TABS[0]);

    readonly context = computed<InsightContext>(() => ({
        scope: 'location',
        id: this.locationId(),
        namespaceId: this.namespaceId(),
        linkedDevices: this.linkedDevices(),
        location: this.location(),
        usageStale: this.usageStale()
    }));

    readonly panels = computed<InsightPanelDef[]>(() => availableInsightPanels(this.allPanels, this.context(), this.tab()));

    readonly range = computed<InsightRange>(() => {
        const requested = Number(this.queryParams().get('range'));
        const hours = RANGE_HOURS.includes(requested) ? requested : RANGE_HOURS[0];
        const to = new Date();
        return { hours, from: new Date(to.getTime() - hours * 3600_000), to };
    });

    readonly rangeOptions = computed(() => [
        { label: this.t()('metrics.range24h'), value: RANGE_HOURS[0] },
        { label: this.t()('metrics.range7d'), value: RANGE_HOURS[1] },
        { label: this.t()('metrics.range30d'), value: RANGE_HOURS[2] }
    ]);

    readonly rangeSubtitle = computed(() => `${this.t()('metrics.range')}: ${insightRangeLabel(this.range().hours, this.t())}`);

    /** The room's live usage as two "now" tiles, then the tiles the panels publish. */
    readonly summaryTiles = computed<InsightSummaryTile[]>(() => {
        if (!this.tabDef().summary) return [];
        const usage = this.location()?.usage;
        const own = usage ? roomUsageSummaryTiles(usage, this.usageStale(), this.t(), (iso) => formatDate(iso, 'shortTime', this.locale)) : [];
        return [...own, ...this.summary.tilesFor(this.panels().map((p) => p.id))];
    });

    setRange(hours: number): void {
        void this.router.navigate([], { relativeTo: this.route, queryParams: { range: hours === RANGE_HOURS[0] ? null : hours }, queryParamsHandling: 'merge' });
    }
}

/** "Occupancy now" and "Availability now" tiles for a room's live usage. */
export function roomUsageSummaryTiles(usage: RoomUsageState, stale: boolean, t: (key: string) => string, time: (iso: string) => string): InsightSummaryTile[] {
    const tiles: InsightSummaryTile[] = [];
    const lastReported = `${t('roomUsage.lastReported')} ${time(usage.updatedUtc)}`;
    if (usage.hasOccupancy) {
        const count = typeof usage.peopleCount === 'number' ? `${usage.peopleCount}${usage.capacity ? ` / ${usage.capacity}` : ''} ${t('roomUsage.people')}` : null;
        const since = usage.occupancyChangedUtc ? `${t('roomUsage.since')} ${time(usage.occupancyChangedUtc)}` : null;
        tiles.push({
            key: 'occupancy-now',
            label: t('insights.occupancyNow'),
            text: t(`roomUsage.state.${usage.occupancy}`),
            icon: usage.occupancy === 'Occupied' ? 'fa fa-user-group' : 'fa fa-door-open',
            caption: stale ? lastReported : [count, since].filter(Boolean).join(' · ') || null
        });
    }
    const line = describeAvailability(usage);
    if (line) {
        let caption: string | null = null;
        if (line.kind === 'inMeeting') caption = [`${t('roomUsage.until')} ${time(line.time)}`, line.title].filter(Boolean).join(' · ');
        else if (line.kind === 'nextAt') caption = [`${t('roomUsage.nextAt')} ${time(line.time)}`, line.title].filter(Boolean).join(' · ');
        else if (line.kind === 'freeUntil') caption = `${t('roomUsage.until')} ${time(line.time)}`;
        else if (line.kind === 'freeRestOfDay') caption = t('roomUsage.freeRestOfDay');
        tiles.push({
            key: 'availability-now',
            label: t('insights.availabilityNow'),
            text: t(`roomUsage.availability.${usage.availability}`),
            icon: 'fa fa-calendar',
            caption: stale ? lastReported : caption
        });
    }
    return tiles;
}

/** Registered panels with data for the context (optionally only one tab's), in display order. */
export function availableInsightPanels(panels: InsightPanelDef[], context: InsightContext, tab?: InsightTab): InsightPanelDef[] {
    return panels.filter((p) => (!tab || p.tab === tab) && p.isAvailable(context)).sort((a, b) => a.order - b.order);
}
